import { ref } from 'vue'
import { defineStore } from 'pinia'
import type { AxiosError } from 'axios'
import { refreshTokens, type TokensResponse } from '@/features/auth/api'
import { useUserStore } from './useUserStore'

interface PersistedTokens {
  accessToken: string | null
  refreshToken: string | null
  expiresIn: number | null
  refreshExpiresIn: number | null
  username: string | null
}

const STORAGE_KEY = 'userTokens'

const isClient = typeof window !== 'undefined'

const getInitialTokens = (): PersistedTokens => {
  const empty: PersistedTokens = {
    accessToken: null,
    refreshToken: null,
    expiresIn: null,
    refreshExpiresIn: null,
    username: null
  }

  if (!isClient) return empty

  try {
    const stored = window.localStorage.getItem(STORAGE_KEY)
    if (!stored) return empty
    const parsed = JSON.parse(stored) as PersistedTokens
    return {
      accessToken: parsed.accessToken ?? null,
      refreshToken: parsed.refreshToken ?? null,
      expiresIn: parsed.expiresIn ?? null,
      refreshExpiresIn: parsed.refreshExpiresIn ?? null,
      username: parsed.username ?? null
    }
  } catch {
    return empty
  }
}

const persistTokens = (tokens: PersistedTokens) => {
  if (!isClient) {
    return
  }
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(tokens))
}

const clearPersistedTokens = () => {
  if (!isClient) {
    return
  }
  window.localStorage.removeItem(STORAGE_KEY)
}

const REFRESH_RETRY_ATTEMPTS = 3
const REFRESH_RETRY_BASE_DELAY_MS = 500
// Matches the proactive timer: a token inside this window is due for refresh
const EXPIRY_LEAD_MS = 2 * 60 * 1000

const BROADCAST_CHANNEL_NAME = 'fair-auth-tokens'
const REFRESH_LOCK_KEY = 'userTokensRefreshLock'
const REFRESH_LOCK_TTL_MS = 15_000
const REFRESH_LOCK_POLL_MS = 250
const TAB_ID = Math.random().toString(36).slice(2)

interface RefreshLock {
  owner: string
  ts: number
}

const readRefreshLock = (): RefreshLock | null => {
  if (!isClient) return null
  try {
    const raw = window.localStorage.getItem(REFRESH_LOCK_KEY)
    return raw ? (JSON.parse(raw) as RefreshLock) : null
  } catch {
    return null
  }
}

const isForeignLockFresh = (): boolean => {
  const lock = readRefreshLock()
  return !!lock && lock.owner !== TAB_ID && Date.now() - lock.ts < REFRESH_LOCK_TTL_MS
}

const writeRefreshLock = () => {
  if (!isClient) return
  window.localStorage.setItem(REFRESH_LOCK_KEY, JSON.stringify({ owner: TAB_ID, ts: Date.now() }))
}

const releaseRefreshLock = () => {
  if (!isClient) return
  if (readRefreshLock()?.owner === TAB_ID) {
    window.localStorage.removeItem(REFRESH_LOCK_KEY)
  }
}

// Window/document listeners and the BroadcastChannel are bound once per tab
// and delegate to the latest store instance, so re-created stores (tests,
// HMR) do not stack up duplicate handlers.
let activeWakeUpHandler: (() => void) | null = null
let activeSyncHandler: (() => void) | null = null
let broadcast: BroadcastChannel | null = null
let listenersBound = false

const bindGlobalListeners = () => {
  if (!isClient || listenersBound) return
  listenersBound = true

  if ('BroadcastChannel' in window) {
    broadcast = new BroadcastChannel(BROADCAST_CHANNEL_NAME)
    broadcast.onmessage = () => activeSyncHandler?.()
  }

  // Fallback for browsers without BroadcastChannel; also catches the social
  // login popup, which writes tokens to localStorage from its own window
  window.addEventListener('storage', (event: StorageEvent) => {
    if (event.key === STORAGE_KEY || event.key === null) {
      activeSyncHandler?.()
    }
  })

  // setTimeout is throttled in background tabs, so the proactive timer can
  // miss the 2-minute window — on wake-up, refresh before the first action
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      activeWakeUpHandler?.()
    }
  })
  window.addEventListener('focus', () => activeWakeUpHandler?.())
}

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))

const getResponseStatus = (error: unknown): number | undefined =>
  (error as AxiosError)?.response?.status

// The refresh endpoint answers 401 for an invalid/expired/revoked refresh
// token ("Invalid token", "Expired token", "Refresh token is missing or
// empty"). Only that proves the session is dead; everything else may be a
// blip that must never log the user out.
const isRefreshTokenRejected = (error: unknown): boolean => getResponseStatus(error) === 401

const isTransientRefreshError = (error: unknown): boolean => {
  const status = getResponseStatus(error)
  // No HTTP status at all — network failure or timeout
  if (status === undefined) return true
  return status >= 500 || status === 408 || status === 429
}

const decodeJwtPayload = (token: string): Record<string, unknown> | null => {
  try {
    const payload = token.split('.')[1]
    if (!payload) return null
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/')
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=')
    return JSON.parse(window.atob(padded)) as Record<string, unknown>
  } catch {
    return null
  }
}

// The refresh SECRET_HASH server-side must be computed with the canonical
// Cognito username. The access token carries it in the `username` claim —
// unlike the persisted profile name, this is correct for social logins
// ("google_…") and survives profile renames.
const getUsernameFromAccessToken = (token: string | null): string | null => {
  if (!token || !isClient) return null
  const payload = decodeJwtPayload(token)
  const username = payload?.username ?? payload?.['cognito:username']
  return typeof username === 'string' && username ? username : null
}

export const useTokenStore = defineStore('tokens', () => {
  const initialTokens = getInitialTokens()

  const accessToken = ref<string | null>(initialTokens.accessToken)
  const refreshToken = ref<string | null>(initialTokens.refreshToken)
  const expiresIn = ref<number | null>(initialTokens.expiresIn)
  const refreshExpiresIn = ref<number | null>(initialTokens.refreshExpiresIn)

  let refreshTimeout: number | null = null

  // Schedule token refresh on store initialization (restores timer after page reload)
  if (isClient && initialTokens.accessToken && initialTokens.expiresIn) {
    // Use queueMicrotask to ensure store is fully initialized before scheduling
    queueMicrotask(() => scheduleTokenRefresh())
  }

  if (isClient) {
    bindGlobalListeners()
  }

  const notifyOtherTabs = () => {
    broadcast?.postMessage('tokens-changed')
  }

  // Adopt whatever another tab persisted: fresher tokens or a logout.
  // Never writes storage or re-broadcasts, so adoption cannot loop.
  const syncFromStorage = () => {
    const stored = getInitialTokens()

    if (!stored.accessToken) {
      if (accessToken.value) {
        resetTokenState()
      }
      return
    }

    const isFresher = !!stored.expiresIn && stored.expiresIn > (expiresIn.value ?? 0)
    if (!isFresher || stored.accessToken === accessToken.value) {
      return
    }

    accessToken.value = stored.accessToken
    refreshToken.value = stored.refreshToken
    expiresIn.value = stored.expiresIn
    refreshExpiresIn.value = stored.refreshExpiresIn
    scheduleTokenRefresh()
  }

  const isNearExpiry = () => !expiresIn.value || Date.now() > expiresIn.value - EXPIRY_LEAD_MS

  const handleWakeUp = () => {
    if (!accessToken.value && !getInitialTokens().accessToken) {
      return
    }

    syncFromStorage()

    if (isNearExpiry()) {
      void updateTokens()
    } else {
      // The timer may have been throttled away while the tab slept — re-arm it
      scheduleTokenRefresh()
    }
  }

  const scheduleTokenRefresh = () => {
    if (refreshTimeout) {
      clearTimeout(refreshTimeout)
      refreshTimeout = null
    }

    if (!expiresIn.value || !isClient) {
      return
    }

    // Refresh 2 minutes before access token expires
    const timeUntilRefresh = expiresIn.value - Date.now() - EXPIRY_LEAD_MS
    if (timeUntilRefresh <= 0) {
      // Token already expired or about to — refresh immediately
      void updateTokens({ force: true })
      return
    }

    refreshTimeout = window.setTimeout(() => {
      void updateTokens({ force: true })
    }, timeUntilRefresh)
  }

  const setTokens = (response: TokensResponse, username?: string) => {
    const stored = getInitialTokens()

    const tokens: PersistedTokens = {
      accessToken: response.access_token,
      expiresIn: response.expires_in * 1000 + Date.now(),
      // With Cognito refresh-token rotation off, a refresh response carries no
      // new refresh token — keep the current one instead of wiping the session.
      refreshToken: response.refresh_token ?? refreshToken.value ?? stored.refreshToken,
      refreshExpiresIn:
        typeof response.refresh_expires_in === 'number'
          ? response.refresh_expires_in * 1000 + Date.now()
          : refreshExpiresIn.value ?? stored.refreshExpiresIn,
      // Canonical claim from the new token first; then the caller's value,
      // then whatever was already persisted
      username:
        getUsernameFromAccessToken(response.access_token) || username || stored.username || null
    }

    accessToken.value = tokens.accessToken
    refreshToken.value = tokens.refreshToken
    expiresIn.value = tokens.expiresIn
    refreshExpiresIn.value = tokens.refreshExpiresIn

    persistTokens(tokens)
    scheduleTokenRefresh()
    notifyOtherTabs()
  }

  const resetTokenState = () => {
    accessToken.value = null
    refreshToken.value = null
    expiresIn.value = null
    refreshExpiresIn.value = null

    if (refreshTimeout) {
      clearTimeout(refreshTimeout)
      refreshTimeout = null
    }
  }

  const clearTokens = () => {
    resetTokenState()
    clearPersistedTokens()
    notifyOtherTabs()
  }

  let refreshInFlight: Promise<void> | null = null

  // Single-flight: every caller (proactive timer, request interceptor,
  // 401 retry) awaits the same refresh; a new one may start only after
  // the previous fully settles. force skips the still-fresh short-circuit —
  // needed when the server already rejected the current token (401 retry).
  const updateTokens = (options?: { force?: boolean }): Promise<void> => {
    if (!refreshInFlight) {
      refreshInFlight = performRefresh(options?.force ?? false).finally(() => {
        refreshInFlight = null
      })
    }
    return refreshInFlight
  }

  // Another tab may be refreshing right now: wait for its result to land in
  // storage instead of spending our (possibly rotated) refresh token too.
  const waitForForeignRefresh = async (): Promise<boolean> => {
    const deadline = Date.now() + REFRESH_LOCK_TTL_MS

    while (Date.now() < deadline) {
      await wait(REFRESH_LOCK_POLL_MS)
      syncFromStorage()
      if (!isNearExpiry()) {
        return true
      }
      if (!isForeignLockFresh()) {
        return false
      }
    }

    return false
  }

  const performRefresh = async (force: boolean): Promise<void> => {
    // A tab seeing fresher tokens in storage never fires its own refresh
    syncFromStorage()
    if (!force && accessToken.value && !isNearExpiry()) {
      return
    }

    const storedTokens = getInitialTokens()

    if (!storedTokens.refreshToken) {
      console.warn('Token refresh skipped: no refresh token available')
      return
    }

    if (storedTokens.refreshExpiresIn && Date.now() > storedTokens.refreshExpiresIn) {
      // The refresh token is past its own TTL — the session is genuinely over
      clearTokens()
      return
    }

    // Username priority: access-token claim > persisted > profile name
    const username =
      getUsernameFromAccessToken(storedTokens.accessToken) ||
      storedTokens.username ||
      useUserStore()?.name
    if (!username) {
      console.warn('Token refresh skipped: no username available')
      return
    }

    if (isForeignLockFresh() && (await waitForForeignRefresh())) {
      return
    }

    writeRefreshLock()
    try {
      await requestRefresh(username, storedTokens.refreshToken)
    } finally {
      releaseRefreshLock()
    }
  }

  const requestRefresh = async (username: string, refreshTokenValue: string): Promise<void> => {
    for (let attempt = 0; attempt < REFRESH_RETRY_ATTEMPTS; attempt += 1) {
      try {
        const { data } = await refreshTokens({
          username,
          refresh_token: refreshTokenValue
        })

        setTokens(data, username)
        return
      } catch (error) {
        if (isRefreshTokenRejected(error)) {
          console.warn('Refresh token rejected by the API, logging out:', error)
          clearTokens()
          return
        }

        const retriable = isTransientRefreshError(error) && attempt < REFRESH_RETRY_ATTEMPTS - 1
        if (!retriable) {
          // Keep the session "stale but present": the next 401 or wake-up
          // triggers another refresh attempt.
          console.warn('Token refresh failed, keeping session for a later retry:', error)
          return
        }

        await wait(REFRESH_RETRY_BASE_DELAY_MS * 2 ** attempt)
      }
    }
  }

  const persistUsername = (username: string) => {
    const stored = getInitialTokens()
    stored.username = username
    persistTokens(stored)
  }

  const isAuthenticated = () => {
    return !!(accessToken.value && expiresIn.value && Date.now() < expiresIn.value)
  }

  // Point the module-level listeners at this (latest) instance
  activeWakeUpHandler = handleWakeUp
  activeSyncHandler = syncFromStorage

  return {
    accessToken,
    refreshToken,
    expiresIn,
    refreshExpiresIn,
    setTokens,
    updateTokens,
    clearTokens,
    isAuthenticated,
    persistUsername,
    scheduleTokenRefresh,
    get refreshTimeout() {
      return refreshTimeout
    }
  }
})

